import { useCallback, useEffect, useState } from 'react';
import Cropper, { type Area } from 'react-easy-crop';
import 'react-easy-crop/react-easy-crop.css';

import { Button } from '@domcrm/embed-ui';

import { getCroppedImageBlob, type CroppedImageBlobLimits } from '../widget-lib/crop-image';

type Props = {
  open: boolean;
  imageSrc: string;
  onOpenChange: (open: boolean) => void;
  onApply: (file: File) => Promise<void>;
  title?: string;
  hint?: string;
  /** После кропа — уменьшить (пиксели), пропорции сохраняются; для мобильного трафика. */
  outputLimits?: CroppedImageBlobLimits;
  onApplyError?: (message: string) => void;
};

export function ImageCropModal({
  open,
  imageSrc,
  onOpenChange,
  onApply,
  title = 'Фото профиля',
  hint,
  outputLimits,
  onApplyError
}: Props) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (open && imageSrc) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
    }
  }, [open, imageSrc]);

  const onCropComplete = useCallback((_: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels);
  }, []);

  async function handleApply(): Promise<void> {
    if (!croppedAreaPixels || !imageSrc) return;
    setApplying(true);
    try {
      const blob = await getCroppedImageBlob(imageSrc, croppedAreaPixels, 0.92, 'image/jpeg', outputLimits);
      const file = new File([blob], 'avatar.jpg', { type: 'image/jpeg' });
      await onApply(file);
      onOpenChange(false);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Не удалось обрезать изображение';
      onApplyError?.(msg);
    } finally {
      setApplying(false);
    }
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-end justify-center bg-black/55 p-0 sm:items-center sm:p-4"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !applying) onOpenChange(false);
      }}
    >
      <div
        className="flex max-h-[min(92vh,900px)] w-full max-w-[min(96vw,720px)] flex-col overflow-hidden rounded-t-sm bg-[var(--bg-elevated)] shadow-lg sm:max-h-[min(92vh,900px)] sm:rounded-sm"
        role="dialog"
        aria-modal="true"
        aria-labelledby="embed-crop-title"
      >
        <div className="border-b border-[var(--border)] px-4 pb-3 pt-4 sm:px-6 sm:pb-4 sm:pt-6">
          <h2 id="embed-crop-title" className="text-lg font-semibold text-[var(--text)]">
            {title}
          </h2>
          <p className="mt-1 text-sm text-[var(--text-muted)]">
            {hint ?? 'Переместите и масштабируйте кадр (1:1), затем нажмите «Обрезать и загрузить».'}
          </p>
        </div>

        {imageSrc ? (
          <div className="relative mx-3 min-h-[min(52vh,420px)] flex-1 overflow-hidden rounded-sm bg-[var(--bg-soft)] sm:mx-4">
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              cropShape="rect"
              showGrid
              objectFit="horizontal-cover"
            />
          </div>
        ) : null}

        <div className="space-y-2 border-t border-[var(--border)] px-4 py-3 sm:px-6">
          <label className="flex items-center gap-3 text-sm text-[var(--text-muted)]">
            <span className="shrink-0">Масштаб</span>
            <input
              type="range"
              min={1}
              max={3}
              step={0.02}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="h-2 w-full max-w-md accent-[var(--text)]"
              disabled={applying}
              aria-label="Масштаб изображения"
            />
          </label>
        </div>

        <div className="flex flex-col-reverse gap-2 border-t border-[var(--border)] px-4 py-3 sm:flex-row sm:justify-end sm:px-6 sm:py-4">
          <Button type="button" variant="secondary" disabled={applying} onClick={() => onOpenChange(false)}>
            Отмена
          </Button>
          <Button type="button" disabled={applying || !croppedAreaPixels} onClick={() => void handleApply()}>
            {applying ? 'Загрузка…' : 'Обрезать и загрузить'}
          </Button>
        </div>
      </div>
    </div>
  );
}
